import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Brain, Zap, Trophy, ArrowRight, Hash, Type, User, Users, Mail, Loader2, ExternalLink, Sparkles, Rocket, Lightbulb } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import { API } from "../App";
import { authFetch, setAuthToken, getAuthToken } from "../lib/auth";
import { toast } from "sonner";

const SKOOL_PLANS_URL = "https://www.skool.com/memory-school-4598/plans";
const REFERRAL_STORAGE_KEY = "mindforge_ref_code";

const Landing = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(true);
  const [authMode, setAuthMode] = useState(null); // null, 'login', 'trial'
  const [email, setEmail] = useState("");
  const [sendingLink, setSendingLink] = useState(false);
  const [linkSent, setLinkSent] = useState(false);
  const [referralCode, setReferralCode] = useState(null);
  const pollRef = useRef(null);
  const verifyAttempted = useRef(false);

  // Capture ?ref=<code> on mount and persist in localStorage so it survives the magic-link round trip
  useEffect(() => {
    const refFromUrl = searchParams.get("ref");
    if (refFromUrl) {
      const code = refFromUrl.trim().toUpperCase();
      localStorage.setItem(REFERRAL_STORAGE_KEY, code);
      setReferralCode(code);
      // If a trial-signup link arrives via referral, default the form to trial mode
      if (!searchParams.get("token")) {
        setAuthMode("trial");
      }
    } else {
      const stored = localStorage.getItem(REFERRAL_STORAGE_KEY);
      if (stored) setReferralCode(stored);
    }
  }, [searchParams]);

  useEffect(() => {
    // Check for magic link token in URL
    const token = searchParams.get("token");
    if (token && !verifyAttempted.current) {
      verifyAttempted.current = true;
      verifyMagicLink(token);
      return;
    }

    // Check if already authenticated
    const checkAuth = async () => {
      try {
        if (!getAuthToken()) {
          setIsLoading(false);
          return;
        }
        const response = await authFetch(`${API}/auth/me`);
        if (response.ok) {
          navigate("/dashboard", { replace: true });
        }
      } catch (error) {
        // Not authenticated, show landing
      } finally {
        setIsLoading(false);
      }
    };
    
    if (!token) {
      checkAuth();
    }
  }, [navigate, searchParams]);

  const verifyMagicLink = async (token) => {
    try {
      const response = await fetch(`${API}/auth/magic-link/verify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token }),
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.session_token) {
          setAuthToken(data.session_token);
        }
        toast.success("Welcome to MindForge!");
        window.history.replaceState({}, '', '/');
        navigate("/dashboard", { state: { user: data }, replace: true });
      } else {
        const error = await response.json();
        toast.error(error.detail || "Invalid or expired link");
        window.history.replaceState({}, '', '/');
        setIsLoading(false);
      }
    } catch (error) {
      toast.error("Failed to verify link");
      window.history.replaceState({}, '', '/');
      setIsLoading(false);
    }
  };

  const startPolling = (pollId) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const res = await fetch(`${API}/auth/magic-link/poll?poll_id=${pollId}`);
        const data = await res.json();
        if (data.status === "verified" && data.user) {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setAuthToken(data.user.session_token);
          toast.success("Welcome to MindForge!");
          navigate("/dashboard", { state: { user: data.user }, replace: true });
        } else if (data.status === "expired") {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setLinkSent(false);
          toast.error("Magic link expired. Please request a new one.");
        }
      } catch (err) {
        // Silently retry
      }
    }, 3000);
  };

  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  const handleMagicLinkRequest = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error("Please enter your email");
      return;
    }

    const isTrialSignup = authMode === "trial";
    const endpoint = isTrialSignup ? "/auth/signup-trial" : "/auth/magic-link/request";

    setSendingLink(true);
    try {
      const body = { email: email.trim() };
      if (isTrialSignup && referralCode) {
        body.referral_code = referralCode;
      }
      const response = await fetch(`${API}${endpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      const data = await response.json();

      if (response.ok) {
        setLinkSent(true);
        if (isTrialSignup && data.referral_applied) {
          toast.success("Trial link sent! Bonus days locked in 🎁");
        } else {
          toast.success(
            isTrialSignup
              ? "Trial activation link sent! Check your inbox."
              : "Magic link sent! Check your inbox."
          );
        }
        if (data.poll_id) {
          startPolling(data.poll_id);
        }
        // Clear referral code once consumed
        if (isTrialSignup && data.referral_applied) {
          localStorage.removeItem(REFERRAL_STORAGE_KEY);
        }
      } else if (response.status === 409 && data.detail === "memory_school_member") {
        toast.info("You're a Memory School member — please use the login option.");
        setAuthMode("login");
      } else if (response.status === 409 && data.detail === "account_exists") {
        toast.info("You already have an account — please log in.");
        setAuthMode("login");
      } else if (response.status === 403 && data.detail === "not_authorized") {
        toast.error("No account found for that email. Try the free trial instead.");
        setAuthMode("trial");
      } else {
        toast.error(data.detail || "Failed to send link");
      }
    } catch (error) {
      toast.error("Failed to send link");
    } finally {
      setSendingLink(false);
    }
  };

  const handleSignUp = () => {
    window.open(SKOOL_PLANS_URL, "_blank");
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground overflow-hidden">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center">
        {/* Background glow */}
        <div className="absolute inset-0 hero-glow"></div>
        
        {/* Hero image overlay */}
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: "url('https://images.pexels.com/photos/7709020/pexels-photo-7709020.jpeg')",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        
        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 md:px-12 py-24">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
            {/* Text Content */}
            <motion.div 
              className="md:col-span-7"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h1 className="font-heading text-5xl md:text-7xl font-bold tracking-tighter mb-6">
                <span className="text-primary">MIND</span>FORGE
              </h1>
              <p className="text-xl md:text-2xl text-foreground mb-4 max-w-xl leading-relaxed font-medium">
                You don't have a bad memory.<br />You have an <span className="text-primary">untrained</span> one.
              </p>
              <p className="text-base md:text-lg text-muted-foreground mb-6 max-w-xl leading-relaxed">
                Numbers. Words. Names. Faces. The techniques world memory champions actually use — in 20-minute daily reps built by a Grand Master of Memory.
              </p>
              <p className="text-base text-muted-foreground/80 mb-8 max-w-xl">
                Start with a <span className="text-primary font-medium">7-day free trial</span> — or log in if you're a Memory School member.
              </p>

              {/* Auth Options */}
              <AnimatePresence mode="wait">
                {!authMode && (
                  <motion.div 
                    key="buttons"
                    className="flex flex-col gap-4 max-w-md"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                  >
                    <Button 
                      onClick={() => setAuthMode('trial')}
                      data-testid="start-trial-btn"
                      className="bg-primary text-primary-foreground hover:bg-primary/90 px-8 py-6 text-lg font-semibold btn-hover w-full"
                    >
                      <Rocket className="mr-2 w-5 h-5" />
                      Start 7-Day Free Trial
                    </Button>
                    <Button 
                      onClick={() => setAuthMode('login')}
                      variant="outline"
                      data-testid="login-btn"
                      className="px-8 py-6 text-lg font-semibold w-full"
                    >
                      <Mail className="mr-2 w-5 h-5" />
                      I'm already a member — Log in
                    </Button>
                  </motion.div>
                )}

                {/* Login / Trial Form */}
                {(authMode === 'login' || authMode === 'trial') && (
                  <motion.div
                    key={authMode}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                  >
                    <Card className="bg-card/95 backdrop-blur-xl border-border max-w-md">
                      <CardContent className="p-6">
                        {linkSent ? (
                          <div className="text-center py-4">
                            <div className="w-16 h-16 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-4">
                              <Mail className="w-8 h-8 text-primary animate-pulse" />
                            </div>
                            <h3 className="font-heading text-xl font-semibold mb-2">Check your inbox!</h3>
                            <p className="text-muted-foreground">
                              We sent a {authMode === 'trial' ? 'trial activation' : 'magic'} link to
                            </p>
                            <p className="text-foreground font-medium mt-1">{email}</p>
                            <p className="text-sm text-muted-foreground mt-4">
                              Click the link in the email to {authMode === 'trial' ? 'start your free trial' : 'sign in'}.
                              <br />
                              <span className="text-primary/80">This page will update automatically.</span>
                            </p>
                            <div className="flex items-center justify-center gap-1.5 mt-3">
                              <div className="w-1.5 h-1.5 rounded-full bg-primary animate-bounce" style={{animationDelay: '0ms'}} />
                              <div className="w-1.5 h-1.5 rounded-full bg-primary animate-bounce" style={{animationDelay: '150ms'}} />
                              <div className="w-1.5 h-1.5 rounded-full bg-primary animate-bounce" style={{animationDelay: '300ms'}} />
                            </div>
                            <Button
                              variant="ghost"
                              className="mt-4"
                              onClick={() => {
                                if (pollRef.current) clearInterval(pollRef.current);
                                setAuthMode(null);
                                setLinkSent(false);
                                setEmail("");
                              }}
                            >
                              ← Back
                            </Button>
                          </div>
                        ) : (
                          <>
                            <div className="flex items-center justify-between mb-6">
                              <h3 className="font-heading text-xl font-semibold">
                                {authMode === 'trial' ? 'Start your free trial' : 'Login to MindForge'}
                              </h3>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setAuthMode(null)}
                              >
                                ← Back
                              </Button>
                            </div>
                            <form onSubmit={handleMagicLinkRequest}>
                              <Label className="text-sm text-muted-foreground mb-2 block">
                                {authMode === 'trial' ? 'Your email' : 'Memory School Email'}
                              </Label>
                              <Input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="your@email.com"
                                className="bg-background mb-4"
                                data-testid="magic-link-email"
                                autoFocus
                              />
                              <Button 
                                type="submit"
                                disabled={sendingLink}
                                className="w-full bg-primary text-primary-foreground py-5"
                                data-testid={authMode === 'trial' ? "submit-trial-btn" : "send-magic-link-btn"}
                              >
                                {sendingLink ? (
                                  <>
                                    <Loader2 className="w-5 h-5 animate-spin mr-2" />
                                    Sending...
                                  </>
                                ) : authMode === 'trial' ? (
                                  <>
                                    <Rocket className="w-5 h-5 mr-2" />
                                    Send activation link
                                  </>
                                ) : (
                                  <>
                                    <Mail className="w-5 h-5 mr-2" />
                                    Send Magic Link
                                  </>
                                )}
                              </Button>
                              <p className="text-xs text-muted-foreground mt-3 text-center">
                                {authMode === 'trial'
                                  ? "We'll email you a link to verify your address and start your 7-day free trial. No card required."
                                  : "Enter the email you used to join Memory School Premium"}
                              </p>
                              {authMode === 'trial' && referralCode && (
                                <div
                                  className="mt-4 px-3 py-2 bg-primary/10 border border-primary/30 text-xs text-primary flex items-center gap-2 justify-center"
                                  data-testid="referral-applied-banner"
                                >
                                  <span>🎁</span>
                                  <span>
                                    Referral code <span className="font-mono font-semibold">{referralCode}</span> applied — you'll get +7 bonus days
                                  </span>
                                </div>
                              )}
                            </form>
                            
                            {authMode === 'login' && (
                              <div className="mt-6 pt-6 border-t border-border">
                                <p className="text-sm text-muted-foreground text-center mb-3">
                                  Don't have an account yet?
                                </p>
                                <Button
                                  variant="outline"
                                  className="w-full"
                                  onClick={() => { setAuthMode('trial'); setLinkSent(false); }}
                                >
                                  <Rocket className="w-4 h-4 mr-2 text-primary" />
                                  Start 7-Day Free Trial
                                </Button>
                              </div>
                            )}
                            {authMode === 'trial' && (
                              <div className="mt-6 pt-6 border-t border-border">
                                <p className="text-sm text-muted-foreground text-center mb-3">
                                  Already a Memory School member?
                                </p>
                                <Button
                                  variant="outline"
                                  className="w-full"
                                  onClick={() => { setAuthMode('login'); setLinkSent(false); }}
                                >
                                  <Mail className="w-4 h-4 mr-2 text-primary" />
                                  Log in instead
                                </Button>
                              </div>
                            )}
                          </>
                        )}
                      </CardContent>
                    </Card>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>

            {/* Floating Stats */}
            <motion.div 
              className="md:col-span-5"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="space-y-4">
                <div className="bg-card/80 backdrop-blur-xl border border-border p-6 card-hover">
                  <div className="font-mono text-3xl md:text-4xl text-lime-400 tracking-widest mb-2">
                    48293750182
                  </div>
                  <p className="text-muted-foreground text-sm">20 digits per line</p>
                </div>
                <div className="bg-card/80 backdrop-blur-xl border border-border p-6 card-hover">
                  <div className="text-xl text-secondary mb-2">
                    Algorithm • Philosophy • Metamorphosis
                  </div>
                  <p className="text-muted-foreground text-sm">Simple to complex words</p>
                </div>
                <div className="bg-card/80 backdrop-blur-xl border border-border p-6 card-hover">
                  <div className="text-xl text-orange-500 mb-2">
                    Sarah • Michael • Emma
                  </div>
                  <p className="text-muted-foreground text-sm">Remember names with faces</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-3xl md:text-5xl font-bold tracking-tight mb-3 leading-tight">
              Keep your memory <span className="text-primary">sharp.</span>
            </h2>
            <p className="text-lg md:text-xl text-muted-foreground">
              Four ways to train.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Number Recall */}
            <motion.div
              className="bg-card border border-border p-6 md:p-7 card-hover flex flex-col"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <div className="w-14 h-14 bg-lime-500/10 flex items-center justify-center mb-6">
                <Hash className="w-7 h-7 text-lime-400" />
              </div>
              <h3 className="font-heading text-2xl font-semibold mb-4">Number Recall</h3>

              <div className="flex-1 mb-5">
                <div className="flex items-start gap-2 mb-1">
                  <span className="text-xl flex-shrink-0 leading-7">🤯</span>
                  <p className="text-base text-foreground/90 leading-7">
                    Start at <span className="font-bold text-foreground">20</span>.
                  </p>
                </div>
                <p className="text-base text-foreground/90 leading-7 ml-9 mb-3">
                  Push to <span className="font-bold text-lime-400">200</span>.
                </p>
                <p className="text-sm text-muted-foreground italic ml-9 min-h-[20px]">
                  (Yes, two hundred. In one go.)
                </p>
              </div>

              <div className="pt-4 border-t border-border/60 min-h-[180px]">
                <div className="flex gap-2">
                  <Lightbulb className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    <span className="text-foreground font-medium">Why it matters:</span> Be the person in the room with the data. Stats, figures, key numbers — at your fingertips, not in your notes. That's how you influence.
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Word Memory */}
            <motion.div
              className="bg-card border border-border p-6 md:p-7 card-hover flex flex-col"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <div className="w-14 h-14 bg-secondary/10 flex items-center justify-center mb-6">
                <Type className="w-7 h-7 text-secondary" />
              </div>
              <h3 className="font-heading text-2xl font-semibold mb-4">Word Memory</h3>

              <div className="flex-1 mb-5">
                <div className="flex items-start gap-2 mb-1">
                  <span className="text-xl flex-shrink-0 leading-7">🤯</span>
                  <p className="text-base text-foreground/90 leading-7">
                    Start at <span className="font-bold text-foreground">10</span> words.
                  </p>
                </div>
                <p className="text-base text-foreground/90 leading-7 ml-9 mb-3">
                  Push to <span className="font-bold text-secondary">50</span>.
                </p>
                <p className="text-sm text-muted-foreground italic ml-9 min-h-[20px]">
                  (Most people tap out at 7.)
                </p>
              </div>

              <div className="pt-4 border-t border-border/60 min-h-[180px]">
                <div className="flex gap-2">
                  <Lightbulb className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    <span className="text-foreground font-medium">Why it matters:</span> Words are the building blocks of remembering anything. Lock these in and lists, ideas, key points and presentations stop slipping away.
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Names & Faces */}
            <motion.div
              className="bg-card border border-border p-6 md:p-7 card-hover flex flex-col"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
            >
              <div className="w-14 h-14 bg-orange-500/10 flex items-center justify-center mb-6">
                <User className="w-7 h-7 text-orange-500" />
              </div>
              <h3 className="font-heading text-2xl font-semibold mb-4">Names &amp; Faces</h3>

              <div className="flex-1 mb-5">
                <div className="flex items-start gap-2 mb-1">
                  <span className="text-xl flex-shrink-0 leading-7">🤯</span>
                  <p className="text-base text-foreground/90 leading-7">
                    Start with <span className="font-bold text-foreground">5</span> faces.
                  </p>
                </div>
                <p className="text-base text-foreground/90 leading-7 ml-9 mb-3">
                  Build to <span className="font-bold text-orange-500">50</span>.
                </p>
                <p className="text-sm text-muted-foreground italic ml-9 min-h-[20px]">
                  (A whole networking event.)
                </p>
              </div>

              <div className="pt-4 border-t border-border/60 min-h-[180px]">
                <div className="flex gap-2">
                  <Lightbulb className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    <span className="text-foreground font-medium">Why it matters:</span> Remember someone, and they remember you. The fastest way to build rapport — at networking events, meetings, and everywhere it counts.
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Compete */}
            <motion.div
              className="bg-card border border-primary/30 p-6 md:p-7 card-hover flex flex-col relative overflow-hidden"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
            >
              <div className="absolute top-3 right-3 px-2 py-0.5 bg-primary/15 text-primary text-[10px] font-bold tracking-widest uppercase rounded-sm">
                NEW
              </div>
              <div className="w-14 h-14 bg-primary/10 flex items-center justify-center mb-6">
                <Users className="w-7 h-7 text-primary" />
              </div>
              <h3 className="font-heading text-2xl font-semibold mb-4">Compete</h3>

              <div className="flex-1 mb-5">
                <div className="flex items-start gap-2 mb-1">
                  <Trophy className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                  <p className="text-base text-foreground/90 leading-7">
                    Solo gets you started.
                  </p>
                </div>
                <p className="text-base leading-7 ml-9 mb-3">
                  <span className="font-bold text-primary">Friends keep you going.</span>
                </p>
                <p className="text-sm text-muted-foreground italic ml-9 min-h-[20px]">
                  &nbsp;
                </p>
              </div>

              <div className="pt-4 border-t border-border/60 min-h-[180px]">
                <div className="flex gap-2">
                  <Lightbulb className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    <span className="text-foreground font-medium">Why it matters:</span> Training with friends is more fun, more sticky, and the surest way to keep your memory sharp for life. Bring people in. Push each other. Stay sharp together.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Science Teaser Section */}
      <section className="py-24 px-6 md:px-12 border-t border-border/30 relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.04]">
          <div
            style={{
              backgroundImage:
                "radial-gradient(circle at 25% 30%, rgba(221,59,173,0.5) 0%, transparent 45%), radial-gradient(circle at 75% 70%, rgba(132,204,22,0.5) 0%, transparent 45%)",
              width: "100%",
              height: "100%",
            }}
          />
        </div>
        <div className="relative z-10 max-w-3xl mx-auto" data-testid="science-teaser">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-flex items-center gap-2 px-3 py-1 bg-primary/10 text-primary text-xs uppercase tracking-widest font-semibold rounded-full mb-6">
              <Brain className="w-3.5 h-3.5" />
              The Science Behind It
            </span>
            <h2 className="font-heading text-4xl md:text-6xl font-bold tracking-tight mb-6 leading-tight">
              🧠 Your brain isn't{" "}
              <span className="text-primary">the problem.</span>
            </h2>
            <div className="space-y-5 text-lg md:text-xl leading-relaxed text-muted-foreground mb-8">
              <p>
                In <span className="text-foreground font-medium">2017</span>, scientists scanned the brains of the world's top memory champions. They expected to find something special.
              </p>
              <p className="text-foreground font-medium">
                They didn't.
              </p>
              <p>
                The champions' brains looked exactly like everyone else's. So they trained{" "}
                <span className="text-foreground font-medium">51 ordinary people</span> for six weeks.
              </p>
              <p className="font-heading text-2xl md:text-3xl text-primary font-semibold">
                The brains changed.
              </p>
            </div>
            <Button
              onClick={() => navigate("/science")}
              data-testid="science-teaser-cta"
              variant="outline"
              className="px-8 py-6 text-lg font-semibold group"
            >
              Read the science behind MindForge
              <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6 md:px-12 relative">
        <div 
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: "url('https://images.pexels.com/photos/8680103/pexels-photo-8680103.jpeg')",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
          >
            <Brain className="w-16 h-16 text-primary mx-auto mb-8" />
            <h2 className="font-heading text-3xl md:text-5xl font-bold tracking-tight mb-6 leading-tight">
              In 6 weeks, your brain could look like a <span className="text-primary">memory champion's</span>.
            </h2>
            <p className="text-xl text-muted-foreground mb-4">
              The science says so. The only question is whether you train it.
            </p>
            <p className="text-base text-muted-foreground/80 mb-8">
              20 minutes a day. The same techniques the world's top 50 actually use.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={() => {
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                  setTimeout(() => setAuthMode('trial'), 300);
                }}
                data-testid="cta-trial-btn"
                className="bg-primary text-primary-foreground hover:bg-primary/90 px-10 py-6 text-lg font-semibold btn-hover"
              >
                <Rocket className="mr-2 w-5 h-5" />
                Start Free Trial
              </Button>
              <Button 
                onClick={() => {
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                  setTimeout(() => setAuthMode('login'), 300);
                }}
                variant="outline"
                data-testid="cta-login-btn"
                className="px-10 py-6 text-lg font-semibold"
              >
                <Mail className="mr-2 w-5 h-5" />
                I'm a member — Log in
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-border">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="font-heading text-xl font-bold">
            <span className="text-primary">MIND</span>FORGE
          </div>
          <p className="text-muted-foreground text-sm">
            © 2025 MindForge. Train smarter.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
